/**
 * Cómo se escribe cada dato en el cuaderno: fechas «dd/mm/aaaa», decimales con
 * coma, «TODAS» en Id. parcelas… Lo usan el PDF de la Junta y su Excel, para que
 * los dos digan exactamente lo mismo.
 *
 * El cuaderno va SIEMPRE en español (D15), sea cual sea el idioma de la app: es
 * un documento para la Administración andaluza, no una pantalla.
 */
import type { ParcelasRef, ProductoCuaderno } from "./types";

type Maybe<T> = T | null | undefined;

/** «2026-09-12» → «12/09/2026». Una fecha mal formada sale tal cual (nunca se inventa otra). */
export function fecha(iso: Maybe<string>): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** Una fecha o un intervalo: «12/09/2026» o «15/09/2026 – 16/09/2026». */
export function intervalo(i: Maybe<{ desde?: string; hasta?: Maybe<string> }>): string {
  if (!i?.desde) return "";
  if (!i.hasta || i.hasta === i.desde) return fecha(i.desde);
  return `${fecha(i.desde)} – ${fecha(i.hasta)}`;
}

const nf = new Map<string, Intl.NumberFormat>();
function formatter(min: number, max: number): Intl.NumberFormat {
  const k = `${min}-${max}`;
  let f = nf.get(k);
  if (!f) nf.set(k, (f = new Intl.NumberFormat("es-ES", { minimumFractionDigits: min, maximumFractionDigits: max })));
  return f;
}

/** Cifra en español: coma decimal y, desde 10.000, punto de miles. */
export function num(v: Maybe<number>, maxDecimals = 2, minDecimals = 0): string {
  if (v == null || !Number.isFinite(v)) return "";
  return formatter(minDecimals, maxDecimals).format(v);
}

/** Superficie en ha: al menos dos decimales («0,85», «3,20»), hasta cuatro. */
export const hectareas = (v: Maybe<number>) => num(v, 4, 2);

// (Los tipos admiten campos opcionales: con strictNullChecks apagado, zod los infiere así.)
export const dosis = (d: Maybe<{ valor?: number; unidad?: string }>) => (d?.valor != null ? `${num(d.valor, 3)} ${d.unidad ?? ""}`.trim() : "");
export const cantidad = (c: Maybe<{ valor?: number; unidad?: string }>) => (c?.valor != null ? `${num(c.valor, 3)} ${c.unidad ?? ""}`.trim() : "");

/** «TODAS» o los nº de orden separados por comas, de menor a mayor. */
export function parcelas(p: Maybe<ParcelasRef>): string {
  if (!p) return "";
  if (p === "TODAS") return "TODAS";
  return [...new Set(p)].sort((a, b) => a - b).join(", ");
}

/** «Nombre comercial / Sustancia activa» (D15: corto; el formulado completo del MAPA no cabe). */
export function producto(p: Maybe<ProductoCuaderno>): string {
  if (!p) return "";
  return p.sustanciaActiva ? `${p.nombreComercial} / ${p.sustanciaActiva}` : p.nombreComercial;
}

/** Para una celda estrecha: primero «Nombre / sustancia» y, si no cabe, solo el nombre comercial. */
export function productoCelda(p: Maybe<ProductoCuaderno>): string[] {
  if (!p) return [];
  return p.sustanciaActiva ? [producto(p), p.nombreComercial] : [p.nombreComercial];
}

export const registro = (p: Maybe<ProductoCuaderno>) => p?.numeroRegistro ?? "";

const EFICACIA: Record<string, string> = { buena: "Buena", regular: "Regular", mala: "Mala" };
export const eficacia = (e: Maybe<string>) => (e ? EFICACIA[e] ?? e : "");

export const siNo = (b: Maybe<boolean>) => (b == null ? "" : b ? "SI" : "NO");
export const cruz = (b: Maybe<boolean>) => (b ? "X" : "");

/** Nº de orden como texto (vacío si no hay). */
export const orden = (n: Maybe<number>) => (n == null ? "" : String(n));

export const txt = (s: Maybe<string>) => s ?? "";

const SISTEMAS_RIEGO = [
  "",
  "Superficie o gravedad",
  "Aspersión fija",
  "Aspersión móvil",
  "Microaspersión",
  "Nebulización",
  "Goteo",
  "Hidroponía a solución perdida",
  "Hidroponía con recirculación",
];
/** Sistema de riego del apartado 8: «6. Goteo». */
export const sistemaRiego = (n: Maybe<number>) => (n ? `${n}. ${SISTEMAS_RIEGO[n] ?? ""}`.trim() : "");
